import ExecFunctioningImage from "@appally/images/graphic_exec-functioning_small.png";
import JobProfileImage from "@appally/images/graphic_job-profile_small.png";
import PersonalDetailsImage from "@appally/images/graphic_personal-details_small.png";
import { Text } from "@chakra-ui/react";
import { JSX } from "react";

type QuestionType =
  | "text"
  | "textarea"
  | "radio"
  | "checkbox"
  | "dropdown"
  | "slider"
  | "slider-options";

type QuestionBase<Type extends QuestionType, T extends {}> = {
  id: string;
  title: string;
  subtitle?: string;
  type: Type;
  related?: string;
  related_responses?: string[];
  alex?: JSX.Element[];
} & T;

type TextQuestion = QuestionBase<
  "text",
  {
    inputType?: "text" | "date" | "tel" | "email";
    placeholder?: string;
  }
>;

type TextAreaQuestion = QuestionBase<
  "textarea",
  {
    placeholder?: string;
  }
>;

type DropdownQuestion = QuestionBase<
  "dropdown",
  {
    options: string[];
  }
>;

type RadioQuestion = QuestionBase<
  "radio",
  {
    conditional?: true;
    options: string[];
    flow?: "column" | "row";
    align?: "center";
  }
>;

type CheckboxQuestion = QuestionBase<
  "checkbox",
  {
    answerType: "list";
    options: string[];
    flow?: "column" | "row";
  }
>;

type SliderQuestion = QuestionBase<
  "slider",
  { label: string; sliderMin: number; sliderMax: number }
>;

type Question =
  | TextQuestion
  | TextAreaQuestion
  | DropdownQuestion
  | RadioQuestion
  | CheckboxQuestion
  | SliderQuestion;

type Page = {
  title: string;
  id?: string;
  section?: string;
  section_id?: string;
  subtitle?: string;
  questions: Question[];
};

type Sections = Record<
  "about_you" | "about_your_job" | "workplace_challenges",
  {
    title: string;
    id: string;
    description: string;
    image: string;
    link: string;
    time: string;
    pages: Page[];
  }
>;

export const Sections: Sections = {
  about_you: {
    title: "About you",
    id: "about_you",
    description: "Please provide us with a few details about yourself.",
    image: PersonalDetailsImage,
    link: "/about-you/",
    time: "2-5 minutes",
    pages: [
      {
        title: "Let’s fill in a few extra details about you.",
        questions: [
          {
            title: "Pronouns",
            id: "pronouns",
            type: "dropdown",
            options: ["He/him", "She/her", "They/them"],
          },
          {
            title: "Home address line 1",
            id: "address",
            type: "text",
            inputType: "text",
          },
          {
            title: "Home address line 2",
            id: "address_2",
            type: "text",
            inputType: "text",
          },
          {
            title: "City",
            id: "city",
            type: "text",
            inputType: "text",
          },
          {
            title: "Postcode",
            id: "postcode",
            type: "text",
            inputType: "text",
          },
          {
            title: "Date of birth",
            id: "dob",
            type: "text",
            inputType: "date",
          },
          {
            title: "Phone number",
            id: "phone",
            type: "text",
            inputType: "tel",
          },
          {
            title: "Best time to contact?",
            subtitle:
              "Include details of the best days and times to contact you.",
            id: "time_to_contact",
            type: "textarea",
          },
          {
            title:
              "Would you like to give permission to a third party representative for an Access to Work application?",
            id: "third_party_permission",
            type: "radio",

            options: ["Yes", "No"],
            conditional: true,
          },

          {
            related: "third_party_permission",
            related_responses: ["Yes"],
            title: "Their full name",
            id: "thid_party_full_name",
            type: "text",
          },
          {
            related: "third_party_permission",
            related_responses: ["Yes"],
            title: "Their relationship to you",
            id: "third_party_relationship",
            type: "text",
          },
          {
            related: "third_party_permission",
            related_responses: ["Yes"],
            title: "Their phone number",
            id: "third_party_phone",
            type: "text",
            inputType: "tel",
          },
          {
            related: "third_party_permission",
            related_responses: ["Yes"],
            title: "Their email address",
            id: "third_party_email",
            type: "text",
            inputType: "email",
          },
        ],
      },
      {
        title: "Your conditions or disabilities",
        questions: [
          {
            title: "Name your conditions or disabilities",
            id: "conditions",
            type: "checkbox",
            answerType: "list",
            options: [
              "ADHD",
              "Autism",
              "Dyslexia",
              "Dysgraphia",
              "Tourettes",
              "Dyspraxia",
              "Dyscaluculia",
            ],
          },
          {
            title:
              "Any other conditions or disabilities you would like to share?",
            id: "conditions_extra",

            type: "textarea",
          },
        ],
      },
      {
        title: "Your mental health conditions",
        questions: [
          {
            title: "Name your mental health conditions",
            id: "mental_health_conditions",
            type: "checkbox",
            answerType: "list",

            options: [
              "Anxiety",
              "Bipolar disorder",
              "Borderline personality disorder",
              "Depression",
              "Panic attacks",
              "Post-traumatic stress disorder (PTSD)",
              "Complex Post-traumatic stress disorder (CPTSD)",
            ],
          },
          {
            title: "Any other conditions you would like to share?",
            id: "mental_health_extra",

            type: "textarea",
          },
        ],
      },
      {
        title: "Your mental health strategies",
        questions: [
          {
            title:
              "What strategies do you currently use to address your mental health needs?",
            subtitle: "Select any that apply.",
            id: "mental_health_strategies",
            type: "checkbox",
            answerType: "list",

            options: ["Medication", "Apps", "Reminders", "Other", "None"],
            alex: [
              <Text mb={2} fontSize="xl">
                These are some of the strategies which are commonly used to
                address mental health needs, but if you have any strategies
                which aren't listed, please share these in the text area
                available. You can also share any other information that you
                think could be useful.
              </Text>,
            ],
          },
          {
            title: "Any details you’d like to share?",
            id: "mental_health_extra_info",
            placeholder:
              "e.g. type of medication, apps used or other strategies you use to help yourself.",
            type: "textarea",
          },
        ],
      },
    ],
  },
  about_your_job: {
    title: "About your job",
    id: "about_your_job",
    link: "/about-your-job/",
    description:
      "Details about your current position, where you work, and what your average work week looks like.",
    time: "2-5 minutes",
    image: JobProfileImage,
    pages: [
      {
        title: "What is your employment status?",
        questions: [
          {
            id: "employment_status",
            title: "What is your employment status?",
            type: "radio",
            flow: "column",

            options: [
              "Employee",
              "Self-employed",
              "Freelance/Contractor",
              "Unemployed",
            ],
            conditional: true,
            alex: [
              <Text mb={2} fontSize="xl">
                Please let us know your employment status so we can make sure
                any recommendations are suitable to your needs.
              </Text>,
            ],
          },
        ],
      },
      {
        title: "Employment",
        questions: [
          {
            title: "Company",
            id: "company",
            type: "text",

            related: "employment_status",
            related_responses: ["Employee"],
          },
          {
            title: "Role/job title",
            id: "role",
            type: "text",

            related: "employment_status",
            related_responses: ["Employee", "Self-employed"],
          },
          {
            title: "Job description",
            subtitle:
              "You can include: skills, responsibilities, day-to-day activities",
            id: "job_description",
            type: "textarea",

            related: "employment_status",
            related_responses: [
              "Employee",
              "Self-employed",
              "Freelance/Contractor",
            ],
          },
          {
            title: "Start date",
            id: "start_date",
            subtitle:
              "If you cannot remember the exact date, give the first day of the month that you started.",
            type: "text",
            inputType: "date",

            related: "employment_status",
            related_responses: [
              "Employee",
              "Self-employed",
              "Freelance/Contractor",
            ],
          },
          {
            title: "Are you a hybrid worker?",
            subtitle:
              "A hybrid worker is someone who splits their time between working from home and working in an office.",
            id: "hybrid_worker",
            type: "radio",

            options: ["Yes", "No"],
            related: "employment_status",
            related_responses: ["Employee"],
          },
          {
            title: "Work address",
            subtitle: "This should be your local office or workplace address.",
            id: "work_address",
            type: "textarea",

            related: "employment_status",
            related_responses: ["Employee", "Self-employed"],
          },
          {
            title: "Work contact name",
            subtitle:
              "This should be a person that can confirm you work there, like a manager or an HR representative.",
            id: "work_contact_name",
            type: "text",

            related: "employment_status",
            related_responses: ["Employee"],
          },
          {
            title: "Their job title",
            id: "work_contact_job_title",
            type: "text",

            related: "employment_status",
            related_responses: ["Employee"],
          },
          {
            title: "Their email address",
            id: "work_contact_email",
            type: "text",
            inputType: "email",

            related: "employment_status",
            related_responses: ["Employee"],
          },
          {
            title: "Their phone number",
            id: "work_contact_phone",
            type: "text",
            inputType: "tel",

            related: "employment_status",
            related_responses: ["Employee"],
          },
          {
            title: "What is your UTR Number?",
            id: "utr_number",
            type: "text",

            related: "employment_status",
            related_responses: ["Self-employed"],
          },
        ],
      },
      {
        title: "What are your normal working hours?",
        subtitle:
          "If your hours can be different each week, give an estimate of the number of hours that you work on average.",
        questions: [
          {
            title: "What are your normal working hours?",
            label: "hours per week",
            id: "hours",

            type: "slider",
            sliderMin: 0,
            sliderMax: 100,
            related: "employment_status",
            related_responses: [
              "Employee",
              "Self-employed",
              "Freelance/Contractor",
            ],
          },
        ],
      },
      {
        title: "What days do you work?",
        subtitle:
          "If your working days vary, do your best to estimate your average working week.",
        questions: [
          {
            title: "What days do you work?",
            id: "days",
            type: "checkbox",
            flow: "column",
            answerType: "list",
            options: [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ],
            related: "employment_status",
            related_responses: [
              "Employee",
              "Self-employed",
              "Freelance/Contractor",
            ],
          },
        ],
      },
      {
        title: "Do you require support to travel to work?",
        questions: [
          {
            title: "Do you require support to travel to work?",
            id: "travel_support",
            type: "radio",

            options: ["Yes", "No"],
            conditional: true,
            align: "center",
            related: "employment_status",
            related_responses: [
              "Employee",
              "Self-employed",
              "Freelance/Contractor",
            ],
          },
          {
            title: "How many days per week do you travel?",
            id: "travel_days",
            type: "slider",
            sliderMin: 0,
            sliderMax: 7,

            label: "days per week",
            related: "travel_support",
            related_responses: ["Yes"],
          },
          {
            title: "How do you travel?",
            id: "travel_how",
            type: "checkbox",
            flow: "column",
            answerType: "list",
            options: ["Public transportation", "Car", "Cycle", "Walk"],
            related: "travel_support",
            related_responses: ["Yes"],
          },
          {
            title: "Please describe the type of support you need",
            id: "travel_support_info",
            type: "textarea",

            placeholder: "e.g. placeholder text goes here",
            related: "travel_support",
            related_responses: ["Yes"],
          },
        ],
      },
    ],
  },

  workplace_challenges: {
    title: `Workplace challenges`,
    id: "workplace_challenges",
    link: "/workplace-challenges/",
    description:
      "This section looks at how you are able to do things that are frequent challenges at work, such as deal with new or difficult situations, communicate, make decisions, prioritise tasks and solve problems.",
    time: "6-8 minutes",
    image: ExecFunctioningImage,
    pages: [
      {
        section: "Communication / Ease when talking",
        section_id: "communication",
        title: "Communication / Ease when talking",
        id: "communication",

        questions: [
          {
            title: "Do you have difficulty talking at work and/or in meetings?",
            id: "talking_at_work",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you have difficulty finding the right words to make a contribution?",
            id: "right_words",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
        ],
      },
      {
        section: "Cognitive flexibility / Dealing with change",
        section_id: "cognitive",
        title: "Cognitive flexibility / Dealing with change",
        id: "cognitive",

        questions: [
          {
            title:
              "Do unplanned or last minute meetings impact your ability to focus on your work?",
            id: "unplanned_meetings",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you have difficulty with interruptions when focusing on a task?",
            id: "interruptions",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
        ],
      },
      {
        section: "Concentration / Focus",
        section_id: "concentration",
        title: "Concentration / Focus",
        id: "concentration",

        questions: [
          {
            title: "Do you find it hard to maintain concentration in meetings?",
            id: "maintain_concentration_meetings",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you have difficulty listening and taking notes at the same time?",
            id: "difficulty_listening",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title: "Do you find it difficult to sit for long periods of time?",
            id: "sit_for_long_periods",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
        ],
      },
      {
        section: "Impulse control / Dealing with distraction ",
        section_id: "impulse",
        title: "Impulse control / Dealing with distraction ",
        id: "impulse",

        questions: [
          {
            title:
              "Do you find it hard to focus and complete a task without becoming distracted?",
            id: "hard_to_focus",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you lose focus when working with lots of tabs or windows open?",
            id: "losing_track",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
        ],
      },
      {
        section: "Written Word / Comprehension",
        section_id: "comprehension",
        title: "Written Word / Comprehension",
        id: "comprehension",

        questions: [
          {
            title: "Do you misinterpret information in work communications?",
            id: "misinterpreting",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title: "Do you have difficulty reading too much text?",
            id: "difficulty_reading",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you have difficulty sticking to the rules of grammar when writing?",
            id: "grammar",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you have difficulty composing and responding to written communications, like emails?",
            id: "emails",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
        ],
      },
      {
        section: "Organisation",
        section_id: "organisation",
        title: "Organisation",
        id: "organisation",

        questions: [
          {
            title:
              "Do you have difficulty keeping a ‘to-do’ list, with all tasks in one place?",
            id: "todo",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you have trouble managing all the different aspects of your role?",
            id: "trouble_managing",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you have difficulty locating and managing emails, documents or files?",
            id: "locating_emails",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
        ],
      },

      {
        section: "Time management",
        section_id: "time_management",
        title: "Time management",
        id: "time_management",

        questions: [
          {
            title:
              "Do you have difficulty keeping track of time while working on a task?",
            id: "keeping_track",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you have difficulty setting aside a block of time to finish a task and sticking to it?",
            id: "setting_aside_time",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
        ],
      },

      {
        section: "Self-regulating",
        section_id: "self_regulating",
        title: "Self-regulating",
        id: "self_regulating",

        questions: [
          {
            title:
              "Do you hyperfocus/spend too much time on a task to the detriment of yourself and others?",
            id: "hyperfocus",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title: "Do you sometimes put off tasks that cause you anxiety?",
            id: "putting_off",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you sometimes react to a situation before thinking it through?",
            id: "reacting",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you take feedback personally and worry about what is being said about you?",
            id: "taking_feedback",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you sometimes have very productive periods where you overwork?",
            id: "overwork",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you sometimes find it hard to monitor or adjust your behaviour in order to achieve goals?",
            id: "adjust_behaviour",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
        ],
      },
      {
        section: "Task Initiation",
        section_id: "task_initiation",
        title: "Task Initiation",
        id: "task_initiation",

        questions: [
          {
            title:
              "Do you avoid more administrative tasks such as form-filling or database entries?",
            id: "administrative",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
        ],
      },
      {
        section: "Working memory",
        section_id: "working_memory",
        title: "Working memory",
        id: "working_memory",

        questions: [
          {
            title: "Do you sometimes forget to do steps in processes?",
            id: "forgetting_steps",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you have difficulty understanding or remembering what has been said without using an aid for memory?",
            id: "remembering",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
        ],
      },

      {
        section: "Processing information",
        section_id: "processing_information",
        title: "Processing information",
        id: "processing_information",
        questions: [
          {
            title:
              "Do you need to have information on what to do repeated to you?",
            id: "needing_repeated",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title:
              "Do you take a long time to process what is being said or written down for you?",
            id: "long_time_process",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
        ],
      },
      {
        section: "Planning and prioritising",
        section_id: "planning",
        title: "Planning and prioritising",
        id: "planning",
        questions: [
          {
            title:
              "Do you have difficulty breaking down projects into actionable tasks?",
            id: "breaking_down_projects",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
          {
            title: "Do you find it hard to know which tasks to do first?",
            id: "tasks_first",
            type: "radio",

            align: "center",
            options: ["Yes", "No"],
          },
        ],
      },
    ],
  },
  // impact: {
  //   title: "Impact",
  //   id: "impact",
  //   link: "/impact/",
  //   description: "How you are affected by your job and its responsiilities.",
  //   time: "6-8 minutes",
  //   image: ImpactImage,
  //   pages: [
  //     {
  //       title: "Wellbeing",
  //       id: "wellbeing",
  //       text: "As well as the wider health benefits, people with high levels of wellbeing are more productive and are more likely to contribute to their communities",
  //       questions: [
  //         {
  //           title: "I feel anxious, stressed and/or overwhelmed...",
  //           id: "stressed",
  //           type: "slider-options",
  //           options: ["Never", "Sometimes", "Often", "Always"],
  //           align: "center",
  //
  //         },
  //         {
  //           title: "I experience burnout...",
  //           id: "burnout",
  //           type: "slider-options",
  //           options: ["Never", "Sometimes", "Often", "Always"],
  //           align: "center",
  //
  //         },
  //       ],
  //     },
  //     {
  //       title: "Psychological safety & feelings",
  //       text: "People need to feel respected and supported in their work. Psycologically safe employees are more likely to stay in a role.",
  //       id: "psychological",
  //       questions: [
  //         {
  //           title: "I feel embarrassed...",
  //           id: "embarrassed",
  //           type: "slider-options",
  //           options: ["Never", "Sometimes", "Often", "Always"],
  //           align: "center",
  //
  //         },
  //         {
  //           title: "My interpersonal relationships are negatively affected...",
  //           id: "interperonal",
  //           type: "slider-options",
  //           options: ["Never", "Sometimes", "Often", "Always"],
  //           align: "center",
  //
  //         },
  //         {
  //           title: "My credibility decreases...",
  //           id: "credibility",
  //           type: "slider-options",
  //           options: ["Never", "Sometimes", "Often", "Always"],
  //           align: "center",
  //
  //         },
  //       ],
  //     },
  //     {
  //       title: "Progression & security",
  //       text: "People need to feel like they have job or income security, and that there are opportunities for growth and development, whether they are working for an organisation or running their own business.",
  //       id: "progression",
  //       questions: [
  //         {
  //           title: "I miss opportunities for recognition...",
  //           title_self_employed: "I feel unable to run my business...",
  //           id: "unable",
  //           type: "slider-options",
  //           options: ["Never", "Sometimes", "Often", "Always"],
  //           align: "center",
  //
  //         },
  //         {
  //           title:
  //             "I miss opportunities for personal growth, development and progression...",
  //           title_self_employed: "My income is unstable...",
  //           id: "income",
  //           type: "slider-options",
  //           options: ["Never", "Sometimes", "Often", "Always"],
  //           align: "center",
  //
  //         },
  //         {
  //           title: "I have poor work/life balance...",
  //           id: "work-life",
  //           type: "slider-options",
  //           options: ["Never", "Sometimes", "Often", "Always"],
  //           align: "center",
  //
  //         },
  //       ],
  //     },
  //     {
  //       title: "Productivity & Performance",
  //       id: "productivity",
  //       questions: [
  //         {
  //           title: "My workload increases...",
  //           id: "workload",
  //           type: "slider-options",
  //           options: ["Never", "Sometimes", "Often", "Always"],
  //           align: "center",
  //
  //         },
  //         {
  //           title: "I lose track of work and/or miss deadlines...",
  //           id: "lose-track",
  //           type: "slider-options",
  //           options: ["Never", "Sometimes", "Often", "Always"],
  //           align: "center",
  //
  //         },
  //         {
  //           title: "I produce poor quality work...",
  //           id: "poor-quality",
  //           type: "slider-options",
  //           options: ["Never", "Sometimes", "Often", "Always"],
  //           align: "center",
  //
  //         },
  //         {
  //           title: "My productivity decreases...",
  //           id: "productivity-decreases",
  //           type: "slider-options",
  //           options: ["Never", "Sometimes", "Often", "Always"],
  //           align: "center",
  //
  //         },
  //       ],
  //     },
  //   ],
  // },
};

// Initialize the result object
const questionIdTitleMap: Record<string, { title: string }> = {};

// Iterate through the nested structure
Object.values(Sections).forEach((category) => {
  if (category.pages) {
    category.pages.forEach((page) => {
      if (page.questions) {
        page.questions.forEach((question) => {
          const { id, title } = question;
          questionIdTitleMap[id] = { title };
        });
      }
    });
  }
});

export const Questions = questionIdTitleMap;
